/**
 * src/services/apiService.js
 * * Pusat untuk semua komunikasi API dengan backend Laravel.
 * File ini mengelola request, headers, token, dan error handling secara terpusat.
 */

// --- Konfigurasi ---
// Pastikan Base URL ini sesuai dengan prefix route admin di backend Anda
const API_BASE_URL = 'https://api.pondoknusantara.com/api/admin'; 
const OLSERA_API_URL = 'https://api.pondoknusantara.com/api/olsera/transactions';

// --- Helper Functions ---

/**
 * Mengambil token autentikasi dari localStorage.
 * @returns {string|null} Token atau null jika tidak ada.
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Fungsi pembungkus (wrapper) untuk semua request API.
 * @param {string} endpoint - Path API setelah base URL (e.g., '/login').
 * @param {string} method - Metode HTTP (GET, POST, PUT, DELETE).
 * @param {object|FormData|null} body - Body request untuk POST/PUT.
 * @param {boolean} isMultipart - Set true jika mengirim FormData (untuk upload file).
 * @returns {Promise<any>}
 */
const request = async (endpoint, method = 'GET', body = null, isMultipart = false) => {
  const token = getAuthToken();
  const headers = {
    'Accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Jangan set Content-Type jika request adalah multipart/form-data
  if (!isMultipart && body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    method: method.toUpperCase(),
    headers,
  };

  if (body && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
    config.body = isMultipart ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const responseData = await response.json().catch(() => null); // Tangani jika response body kosong

    if (!response.ok) {
      // Jika token tidak valid/expired, paksa logout dan reload halaman
      if (response.status === 401 && endpoint !== '/login') {
        console.error("Unauthorized (401) request to:", endpoint, "- Logging out.");
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
        return Promise.reject({
            message: responseData?.message || 'Sesi Anda telah berakhir. Silakan login kembali.',
            status: response.status,
            ...responseData
        });
      }
      // Tolak promise dengan informasi error dari server
      return Promise.reject({
          message: responseData?.message || `Request failed with status ${response.status}`,
          errors: responseData?.errors,
          status: response.status,
          ...responseData
      });
    }
    // Jika berhasil, kembalikan data
    return responseData !== null ? responseData : { success: true, message: 'Operation successful' };

  } catch (error) {
    console.error(`API Service Network Error (${method} ${endpoint}):`, error);
    return Promise.reject({ message: error.message || 'Network error or failed to fetch', status: null });
  }
};

// =========================================================================
// == API Functions (Dikelompokkan berdasarkan Fitur) ==
// =========================================================================

// --- Authentication ---
export const loginAdmin = (email, password) => request('/login', 'POST', { email, password });
export const logoutAdmin = () => request('/logout', 'POST');

// --- Admin Management ---
export const createAdmin = (adminData) => request('/admins', 'POST', adminData);

// --- App Version ---
export const getAppVersion = () => request('/app-version', 'GET');
export const updateAppVersion = (data) => request('/app-version', 'PUT', data);
export const sendAppVersionNotification = (data = {}) => request('/app-version/send-notification', 'POST', data);

// --- Missions ---
export const getAllMissions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/missions?${query}`, 'GET');
};
export const createMission = (missionData) => request('/missions', 'POST', missionData);
export const getMissionById = (id) => request(`/missions/${id}`, 'GET');
export const updateMission = (id, missionData) => request(`/missions/${id}`, 'PUT', missionData);
export const deleteMission = (id) => request(`/missions/${id}`, 'DELETE');

// --- User Profile & Mission Progress ---
export const findUserByPhone = (phoneNumber) => {
  const params = new URLSearchParams({ phone_number: phoneNumber });
  return request(`/users/by-phone?${params.toString()}`, 'GET');
};
export const getUserMissionProgress = (missionId, phoneNumber) => {
  const params = new URLSearchParams({ mission_id: missionId, phone_number: phoneNumber });
  return request(`/user-mission-progress?${params.toString()}`, 'GET');
};
export const updateUserMissionProgress = (updateData) => {
  return request('/user-mission-progress', 'PUT', updateData);
};
export const adminAddManualTransactionAndPoints = (transactionData) => {
  return request('/users/add-manual-points', 'POST', transactionData);
};

// --- Vouchers ---
export const createVoucher = (formData) => request('/vouchers', 'POST', formData, true);
export const getAllVouchers = (params = {}) => { const query = new URLSearchParams(params).toString(); return request(`/vouchers?${query}`, 'GET'); };
export const getVoucherById = (id) => request(`/vouchers/${id}`, 'GET');
export const updateVoucher = (id, formData) => request(`/vouchers/${id}`, 'POST', formData, true);
export const deleteVoucher = (id) => request(`/vouchers/${id}`, 'DELETE');
export const addCodesToVoucher = (id, codesData) => request(`/vouchers/${id}/add-codes`, 'POST', codesData);
export const deleteSpecificCodeFromVoucher = (id, codeData) => request(`/vouchers/${id}/delete-code`, 'POST', codeData);

// --- START: Penambahan Fungsi untuk Incoming Voucher ---
export const submitIncomingVoucher = (voucherData) => request('/vouchers/incoming', 'POST', voucherData);
// --- END: Penambahan Fungsi untuk Incoming Voucher ---


// --- Voucher Transactions ---
export const getAllVoucherTransactions = (params = {}) => { const query = new URLSearchParams(params).toString(); return request(`/voucher-transactions?${query}`, 'GET'); };
export const adminUpdateVoucherTransactionStatus = (transactionId, statusData) => request(`/voucher-transactions/${transactionId}/update-status`, 'POST', statusData);

// --- Events ---
export const getAllEvents = () => request('/events', 'GET');
export const createEvent = (formData) => request('/events', 'POST', formData, true);
export const getEventById = (id) => request(`/events/${id}`, 'GET');
export const updateEvent = (id, formData) => request(`/events/${id}`, 'POST', formData, true);
export const deleteEvent = (id) => request(`/events/${id}`, 'DELETE');

// --- Promos ---
export const getAllPromos = () => request('/promos', 'GET');
export const createPromo = (formData) => request('/promos', 'POST', formData, true);
export const getPromoById = (id) => request(`/promos/${id}`, 'GET');
export const updatePromo = (id, formData) => request(`/promos/${id}`, 'POST', formData, true);
export const deletePromo = (id) => request(`/promos/${id}`, 'DELETE');

// --- Menus ---
export const getAllMenus = () => request('/menus', 'GET');
export const createMenu = (formData) => request('/menus', 'POST', formData, true);
export const getMenuById = (id) => request(`/menus/${id}`, 'GET');
export const updateMenu = (id, formData) => request(`/menus/${id}`, 'POST', formData, true);
export const deleteMenu = (id) => request(`/menus/${id}`, 'DELETE');

export const getUserReportList = (page = 1) => request(`/users/report?page=${page}`, 'GET');
export const getUserReportDetail = (userId) => request(`/users/report/${userId}`, 'GET');

// --- Olsera Transaction Refresh ---
export const refreshOlseraData = async () => {
    const headers = { 'Accept': 'application/json' };
    try {
        const response = await fetch(OLSERA_API_URL, { method: 'GET', headers: headers });
        const responseData = await response.json().catch(() => null);
        if (!response.ok) {
            return Promise.reject({
                message: responseData?.message || `Olsera API request failed with status ${response.status}`,
                status: response.status, ...responseData
            });
        }
        return responseData !== null ? responseData : { success: true, message: 'Olsera refresh successful' };
    } catch (error) {
        console.error(`API Service Network Error (Olsera Refresh):`, error);
        return Promise.reject({ message: error.message || 'Network error fetching Olsera data', status: null });
    }
};